import type { RealtimeClient, RealtimeMessage } from '@/services/realtime/realtime-client'
import { adjustedMessage, firedMessage } from './price-alert-format'
import {
  PriceAlertAdjustedEventSchema,
  PriceAlertFiredEventSchema,
  type PriceAlert,
} from './price-alert-types'

/**
 * Per-user price-alert events on the realtime `user:#<edgeUserId>` channel
 * (`ch: "user"`): `price_alert` when an alert fires, `price_alert_adjusted`
 * when a split moves a resting alert. Each one toasts once (deduped by
 * `event_id`, so a redelivery after a reconnect stays quiet) and refreshes the
 * alert list.
 *
 * After an outage the channel replays missed events (history recovery,
 * docs/superpowers/specs/2026-10-21-centrifugo-realtime-design.md §9): a
 * replayed fired alert older than 5 min is listed but neither toasts nor
 * sounds, and a replayed one never sounds. A live (not replayed) one always
 * toasts and sounds: its age is never judged by the browser clock. A resubscribe that could not recover everything
 * (`recovered: false`) refreshes the list instead.
 */
export const PRICE_ALERT_FIRED = 'price_alert'
export const PRICE_ALERT_ADJUSTED = 'price_alert_adjusted'

/** What a realtime event changes in the cached active list (spec C5). */
export type PriceAlertListChange =
  | { kind: 'fired'; alertId: string }
  | { kind: 'adjusted'; alertId: string; level: number }

/** Apply one change to a cached list in place (an RTK updateQueryData draft). */
export function applyPriceAlertChange(list: PriceAlert[], change: PriceAlertListChange): void {
  const i = list.findIndex((a) => a.id === change.alertId)
  if (i < 0) return
  if (change.kind === 'fired') list.splice(i, 1)
  else list[i].level = change.level
}

export interface PriceAlertNotifierDeps {
  client: Pick<RealtimeClient, 'onMessageType'>
  /** Invalidate the PriceAlerts list tag. */
  invalidate: () => void
  /** Patch the cached active list before the refetch, so a chart line leaves or moves at once. */
  patch?: (change: PriceAlertListChange) => void
  notify: {
    showSuccess: (message: string, options?: { mergeGroup: string }) => void
    showInfo: (message: string, options?: { mergeGroup: string }) => void
  }
  /** Side effect for a newly fired alert (the sound ping); not for adjustments. */
  onFired?: () => void
  /** Shared across mounts so a remount never re-toasts. */
  seen?: EventDeduper
}

export interface EventDeduper {
  /** True the first time an id is seen. */
  first(id: string): boolean
}

export function createEventDeduper(max = 500): EventDeduper {
  const ids = new Set<string>()
  return {
    first(id) {
      if (ids.has(id)) return false
      ids.add(id)
      if (ids.size > max) ids.delete(ids.values().next().value as string)
      return true
    },
  }
}

const MERGE = { mergeGroup: 'price-alert' }

const sharedDeduper = createEventDeduper()

/** A replayed fired alert older than this is listed only: no toast or sound. */
export const PRICE_ALERT_STALE_MS = 5 * 60_000

function isStale(firedAt: string | null | undefined): boolean {
  if (!firedAt) return false
  const age = Date.now() - Date.parse(firedAt)
  return age > PRICE_ALERT_STALE_MS
}

export function startPriceAlertNotifications(deps: PriceAlertNotifierDeps): () => void {
  const seen = deps.seen ?? sharedDeduper

  const onFired = (msg: RealtimeMessage) => {
    if (msg.ch !== 'user') return
    const parsed = PriceAlertFiredEventSchema.safeParse(msg.data)
    if (!parsed.success || !seen.first(parsed.data.event_id)) return
    // Only a replay can be stale; a live publication is new by definition.
    const stale = !!msg.recovered && isStale(parsed.data.fired_at)
    if (!stale) deps.notify.showSuccess(firedMessage(parsed.data), MERGE)
    if (!stale && !msg.recovered) deps.onFired?.()
    // A replayed, stale fire is still a fire.
    deps.patch?.({ kind: 'fired', alertId: parsed.data.alert_id })
    deps.invalidate()
  }

  const onAdjusted = (msg: RealtimeMessage) => {
    if (msg.ch !== 'user') return
    const parsed = PriceAlertAdjustedEventSchema.safeParse(msg.data)
    if (!parsed.success || !seen.first(parsed.data.event_id)) return
    deps.notify.showInfo(adjustedMessage(parsed.data), MERGE)
    if (parsed.data.alert_id != null && parsed.data.level != null) {
      deps.patch?.({
        kind: 'adjusted',
        alertId: parsed.data.alert_id,
        level: parsed.data.level,
      })
    }
    deps.invalidate()
  }

  const offFired = deps.client.onMessageType(PRICE_ALERT_FIRED, onFired, {
    // Missed events the history could not replay: the list is the truth.
    onSubscribed: (ctx) => {
      if (ctx.wasRecovering && !ctx.recovered) deps.invalidate()
    },
  })
  const offAdjusted = deps.client.onMessageType(PRICE_ALERT_ADJUSTED, onAdjusted)
  return () => {
    offFired()
    offAdjusted()
  }
}
